import ExtensionIcon from "@mui/icons-material/Extension";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { usePagesTranslation } from "../../pages/locales";

/**
 * Reusable dialog for displaying a story (title, image, text).
 * Optionally shows jigsaw/sliding navigation icons when those puzzles are PUBLISHED.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   story: { title?: string, imageUrl?: string, text?: string, jigsaw?: { id: string, state?: string }, sliding?: { id: string, state?: string } },
 *   fallbackTitle?: string,
 * }} props
 */
export default function StoryDialog({ open, onClose, story, fallbackTitle }) {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const title = String(story?.title || "").trim();
    const imageUrl = story?.imageUrl || "";
    const text = String(story?.text || "").trim();
    const heading = title || fallbackTitle || t("library.open-story", "Story");

    const jigsawId = story?.jigsaw?.state === "PUBLISHED" ? story.jigsaw.id : null;
    const slidingId = story?.sliding?.state === "PUBLISHED" ? story.sliding.id : null;
    const storyId = story?.id || "";

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{heading}</DialogTitle>
            <DialogContent dividers>
                {/* Puzzle navigation icons */}
                {(jigsawId || slidingId) && storyId && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mb: 1 }}>
                        {slidingId && (
                            <Tooltip title="Sliding Puzzle Quest" arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => { onClose(); navigate(`/puzzle/play-sliding/${storyId}`); }}
                                    sx={{ color: "#3BA666" }}
                                >
                                    <ViewCarouselIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {jigsawId && (
                            <Tooltip title="Jigsaw Muse" arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => { onClose(); navigate(`/puzzle/play-jigsaw/${storyId}`); }}
                                    sx={{ color: "#3BA666" }}
                                >
                                    <ExtensionIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                )}
                {imageUrl ? (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt=""
                        sx={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 1, mb: 2 }}
                    />
                ) : null}
                {text ? (
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                        {text}
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {t("library.story-empty-body", "No story text for this puzzle.")}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="contained">
                    {t("btn.close", "Close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
